import { zodResolver } from '@hookform/resolvers/zod'
import { TriangleAlert } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AuthShell } from '@/components/auth-shell'
import { ForcedPasswordChange } from '@/components/forced-password-change'
import { TotpEnroll } from '@/components/totp-enroll'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authApi, hasPendingFlow, isRateLimited, type AuthResponse } from '@/lib/auth-api'
import { sessionFromResponse, sessionQuery } from '@/lib/auth'

/**
 * A rate-limit hit is not a field error — the code the user typed may be correct;
 * allauth's shared `login_failed` pool is simply exhausted. Render it as a warning,
 * distinct from the "wrong value" destructive style, so the user waits instead of
 * hammering the form (which only deepens the limit). The backend exposes no unblock
 * time, so the copy stays general — no invented minute count.
 */
const RATE_LIMIT_MESSAGE = 'Zbyt wiele prób logowania. Odczekaj kilka minut i spróbuj ponownie.'

type AuthError = { kind: 'error' | 'warning'; message: string }

export const Route = createFileRoute('/login')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; reset?: 'ok'; activated?: 'ok'; expired?: 'ok' } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    reset: search.reset === 'ok' ? 'ok' : undefined,
    activated: search.activated === 'ok' ? 'ok' : undefined,
    expired: search.expired === 'ok' ? 'ok' : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    if (session.isAuthenticated) {
      throw redirect({ to: '/' })
    }
  },
  component: LoginPage,
})

const credentialsSchema = z.object({
  email: z.email('Podaj prawidłowy adres e-mail'),
  password: z.string().min(1, 'Podaj hasło'),
})

const totpSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Kod ma 6 cyfr'),
})

/**
 * Renders an auth error. A `warning` (e.g. rate limit) uses the amber notice style
 * shared across the panel — visually distinct from a destructive field error, so a
 * throttle does not read as "you typed the wrong value".
 */
function AuthErrorNotice({ error }: { error: AuthError }) {
  if (error.kind === 'warning') {
    return (
      <div
        role="alert"
        className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200"
      >
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
        <p>{error.message}</p>
      </div>
    )
  }
  return (
    <p role="alert" className="text-sm text-destructive">
      {error.message}
    </p>
  )
}

function LoginPage() {
  const { redirect: redirectTo, reset, activated, expired } = Route.useSearch()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<'credentials' | 'totp' | 'enroll' | 'password-change'>(
    'credentials',
  )
  const [authError, setAuthError] = useState<AuthError | null>(null)
  const [showMfaHelp, setShowMfaHelp] = useState(false)

  const credentialsForm = useForm({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  })
  const totpForm = useForm({
    resolver: zodResolver(totpSchema),
    defaultValues: { code: '' },
  })

  const finishLogin = (response: AuthResponse) => {
    queryClient.setQueryData(sessionQuery.queryKey, sessionFromResponse(response))
    router.history.push(redirectTo ?? '/')
  }

  const submitCredentials = credentialsForm.handleSubmit(async ({ email, password }) => {
    setAuthError(null)
    const response = await authApi.login({ email, password })
    if (response.meta?.is_authenticated) {
      finishLogin(response)
    } else if (hasPendingFlow(response, 'mfa_authenticate')) {
      setStep('totp')
    } else if (hasPendingFlow(response, 'mfa_enroll')) {
      setStep('enroll')
    } else if (hasPendingFlow(response, 'password_change_required')) {
      setStep('password-change')
    } else if (isRateLimited(response)) {
      setAuthError({ kind: 'warning', message: RATE_LIMIT_MESSAGE })
    } else {
      // TR-113: ujednolicony komunikat, bez wskazywania, który składnik się nie zgadza.
      setAuthError({ kind: 'error', message: 'Nieprawidłowy login lub hasło.' })
    }
  })

  const submitTotp = totpForm.handleSubmit(async ({ code }) => {
    setAuthError(null)
    const response = await authApi.mfaAuthenticate(code)
    if (response.meta?.is_authenticated) {
      finishLogin(response)
    } else if (hasPendingFlow(response, 'password_change_required')) {
      // A forced password change is surfaced only once the second factor is
      // satisfied, so accounts with 2FA reach this pending flow here, not in
      // submitCredentials.
      setStep('password-change')
    } else if (isRateLimited(response)) {
      // The typed code may well be correct — allauth checks the shared `login_failed`
      // limit BEFORE validating the TOTP, so this is a throttle, not a wrong code.
      setAuthError({ kind: 'warning', message: RATE_LIMIT_MESSAGE })
    } else {
      setAuthError({ kind: 'error', message: 'Nieprawidłowy kod. Spróbuj ponownie.' })
    }
  })

  const backToCredentials = async () => {
    await authApi.logout()
    totpForm.reset()
    setAuthError(null)
    setShowMfaHelp(false)
    setStep('credentials')
  }

  return (
    <AuthShell>
      <Card>
        {step === 'credentials' ? (
          <>
            <CardHeader>
              <CardTitle>Zaloguj się</CardTitle>
              <CardDescription>Wpisz adres e-mail i hasło.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitCredentials} className="flex flex-col gap-4" noValidate>
                {reset === 'ok' && (
                  <p className="text-sm text-green-700">
                    Hasło zostało zmienione. Zaloguj się nowym hasłem.
                  </p>
                )}
                {activated === 'ok' && (
                  <p className="text-sm text-green-700">
                    Konto zostało utworzone. Zaloguj się, aby dokończyć konfigurację.
                  </p>
                )}
                {expired === 'ok' && (
                  <p className="text-sm text-muted-foreground">
                    Sesja wygasła. Zaloguj się ponownie.
                  </p>
                )}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    {...credentialsForm.register('email')}
                  />
                  {credentialsForm.formState.errors.email && (
                    <p className="text-sm text-destructive">
                      {credentialsForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password">Hasło</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    {...credentialsForm.register('password')}
                  />
                  {credentialsForm.formState.errors.password && (
                    <p className="text-sm text-destructive">
                      {credentialsForm.formState.errors.password.message}
                    </p>
                  )}
                </div>
                {authError && <AuthErrorNotice error={authError} />}
                <Button type="submit" disabled={credentialsForm.formState.isSubmitting}>
                  Zaloguj
                </Button>
                <Link
                  to="/account/password/reset"
                  className="text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
                >
                  Zapomniałem hasła
                </Link>
              </form>
            </CardContent>
          </>
        ) : step === 'enroll' ? (
          <TotpEnroll
            onSuccess={finishLogin}
            onPasswordChangeRequired={() => setStep('password-change')}
            onSwitchAccount={backToCredentials}
          />
        ) : step === 'password-change' ? (
          <ForcedPasswordChange onSuccess={finishLogin} onLogout={backToCredentials} />
        ) : (
          <>
            <CardHeader>
              <CardTitle>Weryfikacja dwuetapowa</CardTitle>
              <CardDescription>Wpisz kod z aplikacji uwierzytelniającej.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitTotp} className="flex flex-col gap-4" noValidate>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="code">Kod (6 cyfr)</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                    {...totpForm.register('code')}
                  />
                  {totpForm.formState.errors.code && (
                    <p className="text-sm text-destructive">
                      {totpForm.formState.errors.code.message}
                    </p>
                  )}
                </div>
                {authError && <AuthErrorNotice error={authError} />}
                <Button type="submit" disabled={totpForm.formState.isSubmitting}>
                  Zaloguj
                </Button>
                <div className="flex flex-col gap-1.5 text-center text-sm">
                  <button
                    type="button"
                    onClick={() => setShowMfaHelp((value) => !value)}
                    className="text-muted-foreground underline-offset-4 hover:underline"
                  >
                    Nie mam dostępu do aplikacji
                  </button>
                  {showMfaHelp && (
                    <p className="text-xs text-muted-foreground">
                      Poproś innego superadministratora o zresetowanie weryfikacji dwuetapowej na
                      Twoim koncie, a potem skonfiguruj ją ponownie.
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={backToCredentials}
                    className="text-muted-foreground underline-offset-4 hover:underline"
                  >
                    Zaloguj się z innego konta
                  </button>
                </div>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </AuthShell>
  )
}
